import { Icon, Tooltip } from '@cotera/client/app/components';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Skeleton } from '../ui/skeleton';
import {
  ReadonlyWatchable,
  useWatchableValue,
} from '../../actions/framework/watchable';
import { useStackFramesAtPrefix } from '../../hooks/use-stackframes-at-prefix';
import { ATC, DG } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';
import { getStatusIndicatorClassName } from '../../lib/utils';
import { Button } from '../ui/button';
import { TabLink } from '../../tab-manager';

export const RunStatus: React.FC<{
  model: {
    identity: ReadonlyWatchable<{
      rel: DG.Graph.Identity | null;
      col: DG.Graph.Identity | null;
    }>;
  };
}> = ({ model }) => {
  const { rel, col } = useWatchableValue(model.identity);
  if (rel === null || col === null) {
    return null;
  }
  return (
    <ErrorBoundary fallbackRender={({ error: _ }) => null}>
      <Suspense fallback={<Skeleton className="w-2 h-2" />}>
        <SuspensfulStatus rel={rel} col={col} />
      </Suspense>
    </ErrorBoundary>
  );
};

const SuspensfulStatus: React.FC<{
  rel: DG.Graph.Identity;
  col: DG.Graph.Identity;
}> = ({ rel, col }) => {
  const stackFrames = useStackFramesAtPrefix(
    ATC.RunKeyHelpers.derivePrefixForColumn(rel.toString(), col.toString())
  );

  Assert.assert(stackFrames.type === 'group');

  //dont show status if there have been no runs
  if (stackFrames.status === 'none') {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <Tooltip
        side="bottom"
        tooltipContent={
          <div className="flex flex-col gap-2">
            <div className="w-full flex justify-between items-center gap-2 pt-1 mb-1">
              <span className="font-medium">Last Run</span>
              <span className="text-muted-foreground">
                {stackFrames.stats.endAt !== null &&
                stackFrames.stats.endAt !== undefined
                  ? new Date(stackFrames.stats.endAt).toLocaleString()
                  : 'In Progress'}
              </span>
            </div>
            <TabLink
              asChild
              to={`/runs/${ATC.RunKeyHelpers.derivePrefixForColumn(
                rel.toString(),
                col.toString()
              )}`}
            >
              <Button variant="outline" size={'sm'}>
                <Icon icon="activity" />
                View Runs
              </Button>
            </TabLink>
          </div>
        }
      >
        <div
          className={`h-2 w-2 rounded-full ${getStatusIndicatorClassName(
            stackFrames.status
          )}`}
        />
      </Tooltip>
    </div>
  );
};
